import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Brain, AlertTriangle, Target, Activity, Layers, CheckCircle2, XCircle, Clock, Zap } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 21.17 ATHENA (ADRs 058-061): per-module track record dashboard.
// Reads source_track_record table joined per-period. Each module gets a
// card showing 30d / 90d hit rates, attribution weight, sample sizes,
// calibration band, and recommended action.

const SOURCE_LABELS = {
  monetary: 'MONETARIS',
  equity: 'AGORIS',
  vol: 'JANUS',
  flow: 'VENATOR',
  polymarket: 'Polymarket Summary',
  geo: 'Geo Summary',
  convergence: 'Convergence Detector',
  philosophy: 'USER_PHILOSOPHY',
  mnemosyne_observation: 'MNEMOSYNE Observations',
  news: 'News Classifier',
};

const SOURCE_CATEGORIES = {
  monetary: 'specialist',
  equity: 'specialist',
  vol: 'specialist',
  flow: 'specialist',
  polymarket: 'deterministic_summary',
  geo: 'deterministic_summary',
  convergence: 'deterministic_summary',
  philosophy: 'context',
  mnemosyne_observation: 'context',
  news: 'context',
};

const CATEGORY_COLORS = {
  specialist: '#3b82f6',
  deterministic_summary: '#10b981',
  context: '#8b5cf6',
};

function classifyAction(hitRate, sampleSize) {
  if (sampleSize < 5) return { label: 'INSUFFICIENT DATA', color: '#6b7280', detail: 'Need 5+ resolved predictions' };
  if (hitRate == null) return { label: 'UNRESOLVED', color: '#6b7280', detail: 'No resolved predictions yet' };
  if (hitRate >= 0.65) return { label: 'KEEP', color: '#10b981', detail: 'Hit rate well above baseline' };
  if (hitRate >= 0.50) return { label: 'KEEP', color: '#10b981', detail: 'Hit rate at/above baseline' };
  if (hitRate >= 0.40) return { label: 'REVIEW', color: '#f59e0b', detail: 'Below 50% — investigate before continuing to weight' };
  if (hitRate >= 0.30) return { label: 'DOWN-WEIGHT', color: '#f97316', detail: 'Materially below baseline' };
  return { label: 'KILL CANDIDATE', color: '#ef4444', detail: 'Hit rate too low to justify weight' };
}

function classifyCalibration(hitRate, attributionWeight, sampleSize) {
  // Compare hit rate to "expected" — for a well-calibrated source contributing
  // X weight, the hit rate should be at or above 50%. Anything materially
  // below means over-confident contribution.
  if (sampleSize < 5 || hitRate == null) return { label: 'PENDING', color: '#6b7280' };
  if (hitRate >= 0.55 && attributionWeight >= 0.15) return { label: 'WELL-CALIBRATED', color: '#10b981' };
  if (hitRate >= 0.50) return { label: 'NEUTRAL', color: '#6b7280' };
  if (hitRate >= 0.40) return { label: 'OVER-CONFIDENT', color: '#f59e0b' };
  return { label: 'UNDER-PERFORMING', color: '#ef4444' };
}

function ModuleCard({ source, rows }) {
  const label = SOURCE_LABELS[source] || source;
  const category = SOURCE_CATEGORIES[source] || 'context';
  const catColor = CATEGORY_COLORS[category];
  const r30 = rows.find(r => r.period_label === '30d') || {};
  const r90 = rows.find(r => r.period_label === '90d') || {};
  const rAll = rows.find(r => r.period_label === 'all_time') || {};
  const action = classifyAction(r30.attributed_hit_rate, r30.sample_size_resolved || 0);
  const avgWeight = rAll.predictions_contributed_to > 0
    ? (rAll.weighted_correct + rAll.weighted_wrong + rAll.weighted_pending) / rAll.predictions_contributed_to
    : 0;
  const calibration = classifyCalibration(r30.attributed_hit_rate, avgWeight, r30.sample_size_resolved || 0);
  const fmtPct = (v) => v != null ? `${Math.round(v * 100)}%` : 'n/a';
  return (
    <div className="px-3 py-2.5 bg-korinth-bg rounded border border-korinth-border/30">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[11px] font-mono font-bold text-white">{label}</span>
        <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ color: catColor, backgroundColor: catColor + '15' }}>
          {category.replace('_', ' ')}
        </span>
        <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ml-auto"
              style={{ color: action.color, backgroundColor: action.color + '20' }}>
          {action.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-2 text-[10px] font-mono">
        <div>
          <div className="text-korinth-muted">30d hit</div>
          <div className="text-white font-bold">{fmtPct(r30.attributed_hit_rate)}</div>
          <div className="text-korinth-muted">n={r30.sample_size_resolved || 0}</div>
        </div>
        <div>
          <div className="text-korinth-muted">90d hit</div>
          <div className="text-white font-bold">{fmtPct(r90.attributed_hit_rate)}</div>
          <div className="text-korinth-muted">n={r90.sample_size_resolved || 0}</div>
        </div>
        <div>
          <div className="text-korinth-muted">contrib</div>
          <div className="text-white font-bold">{rAll.predictions_contributed_to || 0}</div>
          <div className="text-korinth-muted">avg w={Math.round(avgWeight * 100) / 100}</div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 text-[9px] font-mono">
        <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
              style={{ color: calibration.color, backgroundColor: calibration.color + '15' }}>
          {calibration.label}
        </span>
        <span className="text-korinth-muted italic flex-1 truncate">{action.detail}</span>
      </div>
    </div>
  );
}

// ─── Sprint 21.18 ORACLE: KAIROS INTELLIGENCE section ─────────────────

const TREND_COLORS = {
  improving: '#10b981',
  stable: '#6b7280',
  degrading: '#ef4444',
  insufficient_data: '#9ca3af',
};

function fmtPctOrDash(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Math.round(v * 1000) / 10}%`;
}

function fmtBrierOrDash(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(3);
}

function fmtLiftOrDash(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(2)}×`;
}

function brierVerdict(brier) {
  if (brier == null) return { label: 'NO DATA', color: '#6b7280' };
  if (brier <= 0.10) return { label: 'EXCELLENT', color: '#10b981' };
  if (brier <= 0.18) return { label: 'GOOD', color: '#10b981' };
  if (brier <= 0.25) return { label: 'AT RANDOM', color: '#f59e0b' };
  return { label: 'WORSE THAN RANDOM', color: '#ef4444' };
}

function liftVerdict(lift) {
  if (lift == null) return { label: 'NO DATA', color: '#6b7280' };
  if (lift >= 1.20) return { label: 'STRONG ALPHA', color: '#10b981' };
  if (lift >= 1.05) return { label: 'POSITIVE LIFT', color: '#10b981' };
  if (lift >= 0.95) return { label: 'MARGINAL', color: '#f59e0b' };
  return { label: 'NEGATIVE LIFT', color: '#ef4444' };
}

function CalibrationPlot({ hitRateByBand }) {
  const bands = [
    { label: '50-59', mid: 54.5 },
    { label: '60-69', mid: 64.5 },
    { label: '70-79', mid: 74.5 },
    { label: '80-89', mid: 84.5 },
    { label: '90-100', mid: 94.5 },
  ];
  return (
    <div className="space-y-1.5">
      {bands.map(b => {
        const actual = hitRateByBand?.[b.label];
        const expectedPct = b.mid;
        const actualPct = actual != null ? actual * 100 : null;
        const delta = actualPct != null ? actualPct - expectedPct : null;
        const deltaColor = delta == null ? '#6b7280'
          : Math.abs(delta) <= 5 ? '#10b981'
          : Math.abs(delta) <= 12 ? '#f59e0b'
          : '#ef4444';
        return (
          <div key={b.label} className="flex items-center gap-2 text-[10px] font-mono">
            <span className="text-korinth-muted w-14">{b.label}%</span>
            <div className="flex-1 h-3 bg-korinth-bg rounded-full overflow-hidden relative">
              {/* Expected band marker */}
              <div className="absolute top-0 bottom-0 w-px bg-korinth-muted" style={{ left: `${expectedPct}%` }} />
              {/* Actual hit rate bar */}
              {actualPct != null && (
                <div className="h-full" style={{ width: `${actualPct}%`, backgroundColor: deltaColor + 'cc' }} />
              )}
            </div>
            <span className="w-12 text-right" style={{ color: deltaColor }}>
              {actualPct != null ? `${Math.round(actualPct)}%` : '—'}
            </span>
            <span className="w-14 text-right text-korinth-muted">
              {delta != null ? `${delta > 0 ? '+' : ''}${Math.round(delta)}pp` : ''}
            </span>
          </div>
        );
      })}
      <p className="text-[8px] font-mono text-korinth-muted italic mt-2">Bar = actual hit rate; vertical line = expected (band midpoint). Green ≤5pp deviation; amber ≤12pp; red &gt;12pp.</p>
    </div>
  );
}

function KairosIntelligenceSection({ metrics, resolutionStats, predictionsCount }) {
  if (!metrics || metrics.length === 0) {
    return (
      <ModuleContainer
        title="🎯 KAIROS Intelligence (ORACLE — ADR-063)"
        subtitle="Brier + lift + calibration. Recomputed daily during reflection."
      >
        <div className="px-4 py-6 text-center">
          <Brain className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
          <p className="text-[11px] text-korinth-muted font-mono">No metrics computed yet.</p>
          <p className="text-[10px] text-korinth-muted font-mono mt-1">First row populates after KAIROS reflection cycle (03:00 UTC) once predictions resolve with machine win conditions. Trigger now via POST /api/metrics/recompute.</p>
        </div>
      </ModuleContainer>
    );
  }

  const m30 = metrics.find(m => m.period_window === '30d') || {};
  const m90 = metrics.find(m => m.period_window === '90d') || {};
  const mAll = metrics.find(m => m.period_window === 'all_time') || {};
  const trend = m30.trend_indicator || mAll.trend_indicator || 'insufficient_data';
  const trendColor = TREND_COLORS[trend];
  const brierVerd = brierVerdict(m30.brier_score_overall);
  const liftVerd = liftVerdict(m30.lift_overall);

  return (
    <ModuleContainer
      title="🎯 KAIROS Intelligence (ORACLE — ADR-063)"
      subtitle={`Brier + lift + calibration · ${predictionsCount?.with_machine_condition ?? 0} predictions with machine win conditions`}
    >
      <div className="space-y-4">
        {/* Verdict strip */}
        <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono">
          <span className="px-2 py-1 rounded font-bold uppercase tracking-wider" style={{ color: trendColor, backgroundColor: trendColor + '20' }}>
            trend: {String(trend).replace('_', ' ')}
          </span>
          <span className="px-2 py-1 rounded uppercase tracking-wider" style={{ color: brierVerd.color, backgroundColor: brierVerd.color + '15' }}>
            Brier: {brierVerd.label}
          </span>
          <span className="px-2 py-1 rounded uppercase tracking-wider" style={{ color: liftVerd.color, backgroundColor: liftVerd.color + '15' }}>
            Lift: {liftVerd.label}
          </span>
          <span className="ml-auto text-korinth-muted italic text-[10px]">"is KAIROS getting smarter?" verdict ↑</span>
        </div>

        {/* 3-column overview: 30d / 90d / all_time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[m30, m90, mAll].map((m, idx) => {
            const labels = ['30d', '90d', 'all_time'];
            const label = labels[idx];
            return (
              <div key={label} className="px-3 py-2.5 bg-korinth-bg rounded border border-korinth-border/30">
                <div className="text-[9px] font-mono uppercase tracking-wider text-korinth-muted mb-1.5">{label.replace('_', ' ')}</div>
                <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                  <div>
                    <div className="text-korinth-muted">resolved</div>
                    <div className="text-white font-bold">{m.total_predictions_resolved ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-korinth-muted">hit rate</div>
                    <div className="text-white font-bold">{fmtPctOrDash(m.hit_rate_overall)}</div>
                  </div>
                  <div>
                    <div className="text-korinth-muted">Brier (↓)</div>
                    <div className="text-white font-bold">{fmtBrierOrDash(m.brier_score_overall)}</div>
                  </div>
                  <div>
                    <div className="text-korinth-muted">lift (↑)</div>
                    <div className="text-white font-bold">{fmtLiftOrDash(m.lift_overall)}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-korinth-muted">calibration error</div>
                    <div className="text-white font-bold">{m.calibration_error_overall != null ? `${(m.calibration_error_overall * 100).toFixed(1)}pp` : '—'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Calibration plot */}
        <div>
          <h4 className="text-[10px] font-mono text-korinth-text uppercase tracking-wider mb-2">Calibration plot (30d window)</h4>
          <CalibrationPlot hitRateByBand={m30.hit_rate_by_band} />
        </div>

        {/* Lift by type */}
        {m30.lift_by_type && Object.keys(m30.lift_by_type).length > 0 && (
          <div>
            <h4 className="text-[10px] font-mono text-korinth-text uppercase tracking-wider mb-2">Lift by prediction type (30d)</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(m30.lift_by_type).map(([type, lift]) => {
                const v = liftVerdict(lift);
                return (
                  <div key={type} className="px-2 py-1.5 bg-korinth-bg rounded border border-korinth-border/30 text-[10px] font-mono">
                    <div className="text-korinth-muted text-[9px] uppercase tracking-wider truncate">{type.replace(/_/g, ' ')}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-white font-bold">{fmtLiftOrDash(lift)}</span>
                      <span className="text-[8px] uppercase tracking-wider" style={{ color: v.color }}>{v.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Resolution status */}
        {resolutionStats?.counts && (
          <div>
            <h4 className="text-[10px] font-mono text-korinth-text uppercase tracking-wider mb-2">Resolution pipeline (ADR-062)</h4>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1">
                <Clock className="w-3 h-3 text-korinth-muted" />
                <span className="text-korinth-muted">pending</span>
                <span className="text-white font-bold">{resolutionStats.counts.pending}</span>
              </span>
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-korinth-accent" />
                <span className="text-korinth-muted">correct</span>
                <span className="text-white font-bold">{resolutionStats.counts.resolved_correct}</span>
              </span>
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1">
                <XCircle className="w-3 h-3 text-korinth-danger" />
                <span className="text-korinth-muted">wrong</span>
                <span className="text-white font-bold">{resolutionStats.counts.resolved_wrong}</span>
              </span>
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1">
                <span className="text-korinth-muted">partial</span>
                <span className="text-white font-bold">{resolutionStats.counts.resolved_partial}</span>
              </span>
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-korinth-warning" />
                <span className="text-korinth-muted">failed</span>
                <span className="text-white font-bold">{resolutionStats.counts.resolution_failed}</span>
              </span>
              <span className="px-2 py-1 rounded bg-korinth-bg flex items-center gap-1 ml-auto">
                <span className="text-korinth-muted">w/ machine condition</span>
                <span className="text-korinth-accent font-bold">{resolutionStats.counts.with_machine_condition}</span>
                <span className="text-korinth-muted">/ {resolutionStats.counts.total}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

function AblationSection({ ablationRuns, ablationByModule }) {
  return (
    <ModuleContainer
      title="🔬 Ablation Infrastructure (ORACLE — ADR-064)"
      subtitle="Module value proven by ablation, not attribution. Sprint 21.18 ships infrastructure only — scheduled cycles begin Sprint 21.19."
    >
      {(ablationRuns?.length || 0) === 0 ? (
        <div className="px-4 py-6 text-center">
          <Activity className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
          <p className="text-[11px] text-korinth-muted font-mono">No ablation runs yet.</p>
          <p className="text-[10px] text-korinth-muted font-mono mt-1">Smoke-test via POST /api/ablation/trigger {'{"module":"polymarket","runs":1}'}. Real comparison-quality ablations begin in Sprint 21.19.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Per-module aggregate */}
          {(ablationByModule?.length || 0) > 0 && (
            <div>
              <h4 className="text-[10px] font-mono text-korinth-text uppercase tracking-wider mb-2">By module excluded</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {ablationByModule.map(m => (
                  <div key={m.module_excluded} className="px-2 py-1.5 bg-korinth-bg rounded border border-korinth-border/30 text-[10px] font-mono">
                    <div className="text-korinth-muted text-[9px] uppercase tracking-wider">{m.module_excluded}</div>
                    <div className="text-white font-bold">{m.runs} runs</div>
                    <div className="text-[9px] text-korinth-muted">avg agree {m.avg_agreement != null ? `${(m.avg_agreement * 100).toFixed(0)}%` : '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Recent runs list */}
          <div>
            <h4 className="text-[10px] font-mono text-korinth-text uppercase tracking-wider mb-2">Recent runs (top {ablationRuns.length})</h4>
            <div className="space-y-1">
              {ablationRuns.slice(0, 8).map(r => {
                const statusColor = r.status === 'completed' ? '#10b981' : r.status === 'failed' ? '#ef4444' : '#f59e0b';
                return (
                  <div key={r.id} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono border border-korinth-border/30">
                    <span className="px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider" style={{ color: statusColor, backgroundColor: statusColor + '15' }}>{r.status}</span>
                    <span className="text-white font-bold">−{r.module_excluded}</span>
                    <span className="text-korinth-muted">run {r.cycle_run_index}</span>
                    <span className="text-korinth-muted">·</span>
                    <span className="text-korinth-muted">{r.predictions_generated || 0} preds</span>
                    {r.agreement_rate != null && (
                      <span className="text-korinth-muted">· agree {Math.round(r.agreement_rate * 100)}%</span>
                    )}
                    <span className="text-korinth-muted ml-auto">{r.run_at}</span>
                    {r.duration_ms != null && <span className="text-korinth-muted">{r.duration_ms}ms</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </ModuleContainer>
  );
}

export default function ModulePerformancePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('module-performance'); }, []);

  const [trackRecord, setTrackRecord] = useState([]);
  const [allowedSources, setAllowedSources] = useState([]);
  const [convergences, setConvergences] = useState([]);
  const [audits, setAudits] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  // Sprint 21.18 ORACLE state
  const [kairosMetrics, setKairosMetrics] = useState([]);
  const [resolutionStats, setResolutionStats] = useState(null);
  const [ablationRuns, setAblationRuns] = useState([]);
  const [ablationByModule, setAblationByModule] = useState([]);

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      const tr = await json('/api/source-track-record');
      setTrackRecord(tr?.track_record || []);
      setAllowedSources(tr?.allowed_sources || []);
      const conv = await json('/api/convergence/recent?hours=72&limit=10');
      setConvergences(conv?.convergences || []);
      const aud = await json('/api/devils-advocate/audits?limit=15');
      setAudits(aud?.audits || []);
      setAuditSummary(aud?.summary || null);
      // Sprint 21.18 ORACLE
      const km = await json('/api/metrics/daily');
      setKairosMetrics(km?.metrics || []);
      const rs = await json('/api/predictions/resolution/stats');
      setResolutionStats(rs || null);
      const ar = await json('/api/ablation/runs?limit=20');
      setAblationRuns(ar?.runs || []);
      const as = await json('/api/ablation/stats');
      setAblationByModule(as?.by_module || []);
    }
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []);

  // Group track-record rows by source
  const bySource = {};
  for (const r of trackRecord) {
    if (!bySource[r.source]) bySource[r.source] = [];
    bySource[r.source].push(r);
  }

  // Stable display order: specialists → summaries → context
  const displayOrder = [
    'monetary', 'equity', 'vol', 'flow',
    'polymarket', 'geo', 'convergence',
    'philosophy', 'mnemosyne_observation', 'news',
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Module Performance</h1>
        <span className="text-korinth-muted text-sm">Per-source track record (ADR-059)</span>
      </div>

      {/* Top stats strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Sources tracked:</span> <span className="text-white font-bold">{Object.keys(bySource).length}</span> / {allowedSources.length}</span>
        <span><span className="text-korinth-muted">Convergences (72h):</span> <span className="text-white font-bold">{convergences.length}</span></span>
        <span><span className="text-korinth-muted">DA audits:</span> <span className="text-white font-bold">{auditSummary?.total || 0}</span></span>
        {auditSummary && (
          <span><span className="text-korinth-muted">Killed:</span> <span className="text-korinth-danger font-bold">{auditSummary.killed_count || 0}</span></span>
        )}
        {resolutionStats?.counts && (
          <>
            <span><span className="text-korinth-muted">Resolved:</span> <span className="text-korinth-accent font-bold">{(resolutionStats.counts.resolved_correct || 0) + (resolutionStats.counts.resolved_wrong || 0)}</span></span>
            <span><span className="text-korinth-muted">Pending:</span> <span className="text-white font-bold">{resolutionStats.counts.pending || 0}</span></span>
          </>
        )}
        <span className="text-korinth-muted ml-auto italic">ORACLE forward-only from Sprint 21.18</span>
      </div>

      {/* Sprint 21.18 ORACLE: KAIROS Intelligence section (top-level) */}
      <KairosIntelligenceSection
        metrics={kairosMetrics}
        resolutionStats={resolutionStats}
        predictionsCount={resolutionStats?.counts}
      />

      {/* Module track record */}
      <ModuleContainer
        title="Per-Module Track Record"
        subtitle="Hit rate weighted by attribution. Insufficient data until ~5+ predictions resolve per source."
      >
        {Object.keys(bySource).length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Brain className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
            <p className="text-[11px] text-korinth-muted font-mono">No track-record data yet.</p>
            <p className="text-[10px] text-korinth-muted font-mono mt-1">Per-source attribution starts from Sprint 21.17 deploy. First rows populate after KAIROS daily reflection (03:00 UTC) processes resolved predictions.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {displayOrder.filter(s => bySource[s]).map(s => (
              <ModuleCard key={s} source={s} rows={bySource[s]} />
            ))}
          </div>
        )}
      </ModuleContainer>

      {/* Recent convergences */}
      <ModuleContainer
        title="Recent Convergences (Layer A)"
        subtitle={`${convergences.length} cross-specialist agreements last 72h (ADR-058)`}
      >
        {convergences.length === 0 ? (
          <p className="text-[11px] text-korinth-muted font-mono italic">No convergences detected. Expected when stances diverge — convergence requires 3+ modules aligning ≥75% conviction without contradicting opposites.</p>
        ) : (
          <div className="space-y-1.5">
            {convergences.map(c => {
              const sevColor = c.severity === 'critical' ? '#ef4444' : c.severity === 'high' ? '#f59e0b' : '#eab308';
              const dirColor = c.direction === 'risk_on' || c.direction === 'offensive' ? '#10b981' : c.direction === 'risk_off' || c.direction === 'defensive' ? '#ef4444' : '#6b7280';
              return (
                <div key={c.id} className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                    <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ color: sevColor, backgroundColor: sevColor + '20' }}>{c.severity}</span>
                    <span className="px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: dirColor, backgroundColor: dirColor + '15' }}>{c.direction?.replace('_', ' ')}</span>
                    <span className="text-white font-bold">{c.aligned_count} modules</span>
                    <span className="text-korinth-muted">avg {Math.round(c.avg_conviction)}%</span>
                    <span className="text-korinth-muted ml-auto">{c.detected_at}</span>
                    {c.alerted ? <span className="text-korinth-accent">📱</span> : null}
                  </div>
                  <p className="text-[10px] font-mono text-korinth-text mt-1 italic">{c.interpretation}</p>
                  {(c.related_tickers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.related_tickers.slice(0, 6).map(t => (
                        <span key={t} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg border border-korinth-accent/30 text-korinth-accent">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModuleContainer>

      {/* Devil's advocate audits */}
      <ModuleContainer
        title="Devil's Advocate Audits (Layer D)"
        subtitle={`${audits.length} ≥80%-conviction predictions challenged (ADR-060)`}
      >
        {audits.length === 0 ? (
          <p className="text-[11px] text-korinth-muted font-mono italic">No audits yet. Sub-cycle triggers automatically for any KAIROS prediction with conviction ≥80%.</p>
        ) : (
          <div className="space-y-1.5">
            {audits.map(a => {
              const evidenceColor = a.evidence_strength === 'strong' ? '#ef4444' : a.evidence_strength === 'moderate' ? '#f59e0b' : '#10b981';
              const adjLabel = a.killed ? 'KILLED' : (a.conviction_adjustment === 0 ? 'unchanged' : `${a.conviction_adjustment}pt`);
              return (
                <div key={a.id} className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                    <span className="text-white font-bold">{a.asset || 'n/a'}</span>
                    <span className="text-korinth-muted">{a.direction} · {a.timeframe}</span>
                    <span className="px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: evidenceColor, backgroundColor: evidenceColor + '15' }}>
                      {a.evidence_strength}
                    </span>
                    <span className="text-korinth-muted">
                      conv {Math.round(a.original_conviction)}% → <span className={a.killed ? 'text-korinth-danger font-bold' : 'text-white font-bold'}>{Math.round(a.final_conviction)}%</span>
                    </span>
                    <span className={a.killed ? 'text-korinth-danger font-bold' : a.conviction_adjustment < 0 ? 'text-korinth-warning' : 'text-korinth-muted'}>
                      {adjLabel}
                    </span>
                    <span className="text-korinth-muted ml-auto">{a.audit_at}</span>
                  </div>
                  {(a.counter_arguments || []).length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {a.counter_arguments.slice(0, 3).map((ca, i) => (
                        <li key={i} className="text-[10px] font-mono text-korinth-text leading-relaxed">- {ca}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModuleContainer>

      {/* Sprint 21.18 ORACLE: Ablation infrastructure */}
      <AblationSection ablationRuns={ablationRuns} ablationByModule={ablationByModule} />

      <p className="text-[9px] font-mono text-korinth-muted text-center italic">
        Per-source track record per ADR-059 · convergence detection per ADR-058 · devil's advocate per ADR-060 · specialist promotion gate per ADR-061 · mechanical resolution per ADR-062 · Brier+lift metrics per ADR-063 · ablation as truth detector per ADR-064. Sources accumulate forward-only. Track record + KAIROS metrics recompute daily during reflection (03:00 UTC); resolver runs daily at 23:00 UTC.
      </p>
    </div>
  );
}
